/* eslint-disable import/extensions */
/* eslint-disable no-shadow */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tag, Tooltip, Button, Col, Input, Select } from "antd";
import type { ColumnsType } from "antd/lib/table";

import { useTranslation } from "react-i18next";
import {
  FileSearchOutlined,
  PlusCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { Filters, FiltersCard } from "../../components/Filters";
import { Label } from "../../components/ui";

import api from "../../services/api";

import { useToast } from "../../hooks/toast";
import { useAuth } from "../../hooks/auth";
// eslint-disable-next-line import/extensions
import Table from "../../components/Table";
import UserAvatar from "../../components/UserAvatar";
import ProjectModal from "./ProjectModal";
import { Container } from "./styles";
import { ICustomer } from "../Customers";
import { IUser } from "../Users";
import ProjectTypes from "../../enums/ProjectTypes";
import ProjectStatus from "../../enums/ProjectStatus";
import UserRoles from "../../enums/UserRoles";
import { IDetailedProject, IRequirement } from "../Project";
import IStatusImpediment from "../../interfaces/IStatusImpediment";
import isWarningActivity from "../../utils/isWarningActivity";

const { Search } = Input;

export interface IProject {
  id: string;
  identifier: string;
  short_description: string;
  full_description: string;
  contractor_name: string;
  contractor_email: string;
  contractor_phone_number: string;
  status: string;
  purchase_order: string;
  customer: ICustomer;
  manager: IUser;
  projectType: IProjectType;
  project_type_id: string;
  expected_start: string;
  expected_end: string;
  requirements: IRequirement[];
  public_project: boolean;
}

export interface IProjectType {
  id: string;
  name: string;
  acronym: ProjectTypes;
}

const Projects: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingProjectTypes, setLoadingProjectTypes] = useState(false);
  const [managers, setManagers] = useState<IUser[]>([]);
  const [customers, setCustomers] = useState<ICustomer[]>([]);
  const [projectTypes, setProjectTypes] = useState<IProjectType[]>([]);

  const { addToast } = useToast();
  const { user } = useAuth();
  const { t } = useTranslation(["projects"]);

  // Filters
  const [identifier, setIdentifier] = useState<string | undefined>();
  const [identifierValue, setIdentifierValue] = useState<string | undefined>(
    "",
  );
  const [status, setStatus] = useState(undefined);
  const [managerFilter, setManagerFilter] = useState();
  const [customerFilter, setCustomerFilter] = useState();
  const [projectTypeFilter, setProjectTypeFilter] = useState();
  const [serviceSheetValue, setServiceSheetValue] = useState<
    string | undefined
  >("");
  const [serviceSheet, setServiceSheet] = useState<string | undefined>();
  const [shortDescriptionValue, setShortDescriptionValue] = useState<
    string | undefined
  >("");
  const [shortDescription, setShortDescription] = useState<
    string | undefined
  >();
  const [ethicalProposalNumber, setEthicalProposalNumber] = useState<
    string | undefined
  >();
  const [ethicalProposalNumberValue, setEthicalProposalNumberValue] = useState<
    string | undefined
  >("");

  const [receipt, setReceipt] = useState<string | undefined>();
  const [receiptValue, setReceiptValue] = useState<string | undefined>("");

  const [requestOC, setRequestOC] = useState<string | undefined>();
  const [requestOCValue, setRequestOCValue] = useState<string | undefined>("");

  const [RA, setRA] = useState<string | undefined>();
  const [RAValue, setRAValue] = useState<string | undefined>("");

  const [EVL_CEC_COR, setEVL_CEC_COR] = useState<string | undefined>();
  const [EVL_CEC_CORValue, setEVL_CEC_CORValue] = useState<string | undefined>(
    "",
  );

  const isLoggedUserManager = useMemo(
    () =>
      user.role === UserRoles.Manager || user.role === UserRoles.Administrative,
    [user.role],
  );

  const searchParams = useMemo(() => {
    return {
      identifier,
      status,
      manager_id: managerFilter,
      customer_id: customerFilter,
      project_type_id: projectTypeFilter,
      short_description: shortDescription,
      service_sheet: serviceSheet,
      ethical_proposal_number: ethicalProposalNumber,
      receipt,
      request_oc: requestOC,
      ra: RA,
      evl_cec_cor: EVL_CEC_COR,
    };
  }, [
    identifier,
    status,
    managerFilter,
    customerFilter,
    projectTypeFilter,
    shortDescription,
    ethicalProposalNumber,
    requestOC,
    RA,
    EVL_CEC_COR,
  ]);

  useEffect(() => {
    setLoading(true);
    api
      .get<IProject[]>("/projects", { params: searchParams })
      .then(response => {
        setProjects(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os projetos, tente novamente.",
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    setLoadingManagers(true);
    api
      .get<IUser[]>("/users", {
        params: {
          role: "MANAGER",
        },
      })
      .then(response => {
        setManagers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os gestores, tente novamente.",
        });
      })
      .finally(() => setLoadingManagers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoadingCustomers(true);
    api
      .get<ICustomer[]>("/customers")
      .then(response => {
        setCustomers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os clientes, tente novamente.",
        });
      })
      .finally(() => setLoadingCustomers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoadingProjectTypes(true);
    api
      .get<IProjectType[]>("/project-types")
      .then(response => {
        setProjectTypes(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os gestores, tente novamente.",
        });
      })
      .finally(() => setLoadingProjectTypes(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const managerOptions = useMemo(() => {
    if (!managers) {
      return undefined;
    }

    return managers.map(manager => ({
      label: manager.name,
      value: manager.id,
    }));
  }, [managers]);

  const customersOptions = useMemo(() => {
    if (!customers) {
      return undefined;
    }

    return customers.map(customer => ({
      label: customer.name,
      value: customer.id,
    }));
  }, [customers]);

  const projectTypesOptions = useMemo(() => {
    if (!projectTypes) {
      return undefined;
    }

    return projectTypes.map(projectType => ({
      label: t(`project-types.options.${projectType.acronym}.label`),
      value: projectType.id,
    }));
  }, [projectTypes, t]);

  const statusOptions = useMemo(() => {
    return (Object.keys(ProjectStatus) as (keyof typeof ProjectStatus)[]).map(
      (key, _) => {
        return {
          label: t(`status.options.${ProjectStatus[key]}.label`),
          value: ProjectStatus[key],
        };
      },
    );
  }, [t]);

  const columns: ColumnsType<IProject> = [
    {
      title: t("identifier"),
      dataIndex: "identifier",
    },
    {
      title: t("short_description"),
      dataIndex: "short_description",
    },
    {
      title: t("customer"),
      dataIndex: "customer",
      render: (customer: ICustomer | undefined) => {
        return customer?.name;
      },
    },
    {
      title: t("project-types.label"),
      dataIndex: "projectType",
      render: (projectType: IProjectType | undefined) => {
        return (
          <Tag
            color={t(`project-types.options.${projectType?.acronym}.color`)}
            key={projectType?.acronym}
          >
            {t(`project-types.options.${projectType?.acronym}.label`)}
          </Tag>
        );
      },
    },
    {
      title: t("status.label"),
      dataIndex: "status",
      render: (status: string) => {
        return (
          <Tag color={t(`status.options.${status}.color`)} key={status}>
            {t(`status.options.${status}.label`)}
          </Tag>
        );
      },
    },
    {
      title: t("manager"),
      dataIndex: "manager",
      render: (manager: IUser) => {
        if (manager) {
          return (
            <UserAvatar
              name={manager.name}
              url={manager.avatar_url}
              size={36}
            />
          );
        }
        return <></>;
      },
    },
    {
      title: t("proposal_number_ethic"),
      dataIndex: "proposal_number_ethic",
    },
    {
      title: t("actions.label"),
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) => (
        <>
          <Link to={`/app/projects/${record.id}`}>
            <FileSearchOutlined />
          </Link>
          {isWarningActivity(record.requirements) ? (
            <Tooltip title="Projeto possue atividade(s) com impedimento(s)">
              <Button
                type="link"
                icon={<WarningOutlined />}
                style={{ cursor: "default", height: 16, width: 16 }}
              />
            </Tooltip>
          ) : (
            <></>
          )}
        </>
      ),
    },
  ];

  const handleCloseModal = useCallback((project?: IProject) => {
    if (project) {
      setProjects(oldProjects => [project, ...oldProjects]);
    }
    setModalVisible(false);
  }, []);

  const handleShowFilters = useCallback(
    value => {
      setShowFilters(value);
      // As the 'showFilters' has not changed yet, within this function,
      // its logic is the reverse to clear all filters (false = true)
      if (showFilters) {
        setStatus(undefined);
        setIdentifier(undefined);
        setManagerFilter(undefined);
        setCustomerFilter(undefined);
        setProjectTypeFilter(undefined);
        setServiceSheet(undefined);
        setShortDescription(undefined);
        setReceiptValue(undefined);
        setEthicalProposalNumber(undefined);
        setRequestOC(undefined);
        setReceipt(undefined);
        setRA(undefined);
        setEVL_CEC_COR(undefined);
      }
    },
    [showFilters],
  );

  const extraActions = useMemo(
    () =>
      isLoggedUserManager ? (
        <Tooltip title={t("actions.options.create")}>
          <Button
            icon={<PlusCircleOutlined />}
            type="link"
            size="large"
            onClick={() => setModalVisible(true)}
          />
        </Tooltip>
      ) : (
        <></>
      ),
    [isLoggedUserManager, t],
  );

  const extraFilters = useMemo(
    () =>
      showFilters ? (
        <FiltersCard>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("status.label")}</Label>
            </div>
            <Select
              placeholder={t("status.label")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              options={statusOptions}
              onChange={value => setStatus(value)}
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div style={{ marginBottom: 5 }}>
              <Label color="#575962">{t("identifier")}</Label>
            </div>
            <Search
              placeholder={t("identifier")}
              onSearch={value => {
                setIdentifier(value || undefined);
              }}
              onChange={({ target: { value } }) => {
                setIdentifierValue(value || undefined);
              }}
              value={identifierValue}
              allowClear
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("customer")}</Label>
            </div>
            <Select
              allowClear
              options={customersOptions}
              placeholder={t("customer")}
              loading={loadingCustomers}
              style={{ width: "100%" }}
              onChange={value => setCustomerFilter(value)}
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("project-types.label")}</Label>
            </div>
            <Select
              allowClear
              options={projectTypesOptions}
              placeholder={t("project-types.label")}
              loading={loadingProjectTypes}
              style={{ width: "100%" }}
              onChange={value => setProjectTypeFilter(value)}
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("manager")}</Label>
            </div>
            <Select
              allowClear
              options={managerOptions}
              placeholder={t("manager")}
              loading={loadingManagers}
              style={{ width: "100%" }}
              onChange={value => setManagerFilter(value)}
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div style={{ marginBottom: 5 }}>
              <Label color="#575962">{t("Folha de Serviço")}</Label>
            </div>
            <Search
              placeholder={t("Folha de Serviço")}
              onSearch={value => {
                setServiceSheet(value || undefined);
              }}
              onChange={({ target: { value } }) => {
                setServiceSheetValue(value || undefined);
              }}
              value={serviceSheetValue}
              allowClear
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div style={{ marginBottom: 5 }}>
              <Label color="#575962">{t("Descrição Resumida")}</Label>
            </div>
            <Search
              placeholder={t("Descrição Resumida")}
              onSearch={value => {
                setShortDescription(value || undefined);
              }}
              onChange={({ target: { value } }) => {
                setShortDescriptionValue(value || undefined);
              }}
              value={shortDescriptionValue}
              allowClear
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div style={{ marginBottom: 5 }}>
              <Label color="#575962">{t("Número da Proposta Ética")}</Label>
            </div>
            <Search
              placeholder={t("Número da Proposta Ética")}
              onSearch={value => {
                setEthicalProposalNumber(value || undefined);
              }}
              onChange={({ target: { value } }) => {
                setEthicalProposalNumberValue(value || undefined);
              }}
              value={ethicalProposalNumberValue}
              allowClear
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div style={{ marginBottom: 5 }}>
              <Label color="#575962">{t("Nota Fiscal")}</Label>
            </div>
            <Search
              placeholder={t("Nota Fiscal")}
              onSearch={value => {
                setReceipt(value || undefined);
              }}
              onChange={({ target: { value } }) => {
                setReceiptValue(value || undefined);
              }}
              value={receiptValue}
              allowClear
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div style={{ marginBottom: 5 }}>
              <Label color="#575962">{t("Pedido/OC")}</Label>
            </div>
            <Search
              placeholder={t("Pedido/OC")}
              onSearch={value => {
                setRequestOC(value || undefined);
              }}
              onChange={({ target: { value } }) => {
                setRequestOCValue(value || undefined);
              }}
              value={requestOCValue}
              allowClear
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div style={{ marginBottom: 5 }}>
              <Label color="#575962">{t("RA")}</Label>
            </div>
            <Search
              placeholder={t("RA")}
              onSearch={value => {
                setRA(value || undefined);
              }}
              onChange={({ target: { value } }) => {
                setRAValue(value || undefined);
              }}
              value={RAValue}
              allowClear
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div style={{ marginBottom: 5 }}>
              <Label color="#575962">{t(" EVL / CEC / COR")}</Label>
            </div>
            <Search
              placeholder={t(" EVL / CEC / COR")}
              onSearch={value => {
                setEVL_CEC_COR(value || undefined);
              }}
              onChange={({ target: { value } }) => {
                setEVL_CEC_CORValue(value || undefined);
              }}
              value={EVL_CEC_CORValue}
              allowClear
            />
          </Col>
        </FiltersCard>
      ) : (
        <></>
      ),
    [
      customersOptions,
      identifierValue,
      shortDescriptionValue,
      serviceSheetValue,
      ethicalProposalNumberValue,
      requestOCValue,
      receiptValue,
      RAValue,
      EVL_CEC_CORValue,
      loadingCustomers,
      loadingManagers,
      loadingProjectTypes,
      managerOptions,
      projectTypesOptions,
      showFilters,
      statusOptions,
      t,
    ],
  );

  return (
    <Container>
      <ProjectModal
        visible={modalVisible}
        handleCloseModal={handleCloseModal}
      />
      <Table
        data={projects}
        columns={columns}
        title={
          <>
            Projetos
            <Filters
              showFilters={showFilters}
              setShowFilters={handleShowFilters}
            />
          </>
        }
        extraActions={extraActions}
        footer={() => <></>}
        loading={loading}
        pagination={false}
        extraFilters={extraFilters}
      />
    </Container>
  );
};

export default Projects;
